<template>
  <div
    ref="elementRef"
    :class="$style['daisy-fluent-element']"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
  >
    <slot></slot>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref } from 'vue';

export default defineComponent({
  name: 'FluentBackground',
  props: {
    lightColor: {
      type: String,
      required: false,
      default: 'rgba(255, 255, 255, 0.4)',
    },
    gradientSize: {
      type: Number,
      required: false,
      default: 80,
    },
    enabled: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  setup(props) {
    const elementRef = ref<HTMLElement | undefined>();
    let animationFrame: ReturnType<typeof requestAnimationFrame> | undefined = undefined;
    const isTouchEvent = ref(false);

    onMounted(() => {
      document.body.addEventListener('touchend', onTouch, { passive: true });
    });

    onBeforeUnmount(() => {
      document.body.removeEventListener('touchend', onTouch);

      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    });

    function onTouch() {
      isTouchEvent.value = true;
    }

    function onMouseMove(event: MouseEvent) {
      if (!props.enabled) {
        elementRef.value?.style?.removeProperty?.('background-image');
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const element = elementRef.value;

        if (element === undefined || element === null) {
          return;
        }

        if (isTouchEvent.value) {
          isTouchEvent.value = false;
          return;
        }

        const rect = element.getBoundingClientRect();

        const x = event.pageX - rect.left - window.scrollX;
        const y = event.pageY - rect.top - window.scrollY;

        element.style.setProperty(
          'background-image',
          `radial-gradient(circle ${props.gradientSize}px at ${x}px ${y}px, ${props.lightColor}, rgba(255,255,255,0))`
        );
      });
    }

    function onMouseLeave(_event: MouseEvent) {
      elementRef.value?.style?.removeProperty?.('background-image');
    }

    return {
      elementRef,
      onMouseMove,
      onMouseLeave,
    };
  },
});
</script>

<style lang="postcss" module>
.daisy-fluent-element {
  padding: 2px;
}
</style>
